// roomAlerts — the DECISIONS behind the in-tab alert + sound for group chat
// (WOLFCHAT_CONTRACT.md items 5 and 9).
//
// Everything here is pure. The wiring (one shared realtime subscription, the
// Notification/sound call) lives in services/useRoomAlerts.ts and
// components/NotificationHandler.tsx; the rules live here because they are the
// part that is easy to get subtly wrong and impossible to eyeball in a socket
// handler: "muted, but they said @all" and "my own message must never ping me"
// are one boolean apart.
//
// Not mirrored to mobile (services/chat/MIRROR.md): mobile alerts are real
// pushes decided server-side in netlify/functions/_push-rooms.js. The RULES
// are deliberately the same shape as that file's recipient filter.

import { parseAllMentions, userTokenName } from './orderMentions';
import type { RoomMention } from '../types';

/** How much of a message body an alert shows. WhatsApp's is ~100 chars. */
export const ALERT_PREVIEW_CHARS = 120;

export interface RoomMessageAlertInput {
  /** The message is mine — never alert me about my own typing. */
  mine: boolean;
  /** `room_member_state.muted_until` is in the future for me. */
  muted: boolean;
  /** The body `@`s me by name, or `@all`. Beats mute (Umer, decision 4). */
  mentionsMe: boolean;
  /** This room is the one on screen AND the tab has focus — I am reading it. */
  roomOpenAndFocused: boolean;
}

/**
 * Should a `room_messages` INSERT raise the in-tab alert + sound?
 *
 * Order matters: `mine` and `roomOpenAndFocused` are absolute suppressions
 * (a mention in the room I am staring at is still not worth a popup), while
 * `muted` is the only one a mention overrides.
 */
export function shouldAlertForRoomMessage(input: RoomMessageAlertInput): boolean {
  if (input.mine) return false;
  if (input.roomOpenAndFocused) return false;
  if (input.muted && !input.mentionsMe) return false;
  return true;
}

export interface ReactionAlertInput {
  /** The reacted-to message was sent by me — the only reason to alert at all. */
  mineMessage: boolean;
  /** I reacted to my own message. */
  reactorIsMe: boolean;
  muted: boolean;
  roomOpenAndFocused: boolean;
}

/** Should a reaction on MY message raise the alert (contract item 5)? */
export function shouldAlertForReaction(input: ReactionAlertInput): boolean {
  if (!input.mineMessage) return false;
  if (input.reactorIsMe) return false;
  if (input.roomOpenAndFocused) return false;
  // A muted room is muted for reactions too — there is no "@all" equivalent
  // that could override it.
  if (input.muted) return false;
  return true;
}

/** One-line preview of a body for an alert / a list row. */
export function alertPreview(
  body: string | null | undefined,
  opts: { hasAttachment?: boolean; max?: number } = {},
): string {
  const max = opts.max ?? ALERT_PREVIEW_CHARS;
  const text = (body ?? '').replace(/\s+/g, ' ').trim();
  if (!text) return opts.hasAttachment ? '📎 Attachment' : 'New message';
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

export interface AlertContent { title: string; body: string }

/** Title = the room name, body = "Sender: preview" (contract item 9). */
export function roomMessageAlertContent(input: {
  roomName?: string;
  senderName?: string;
  body?: string | null;
  hasAttachment?: boolean;
}): AlertContent {
  const preview = alertPreview(input.body, { hasAttachment: input.hasAttachment });
  const sender = (input.senderName ?? '').trim();
  return {
    title: `💬 ${(input.roomName ?? '').trim() || 'Group'}`,
    body: sender ? `${sender}: ${preview}` : preview,
  };
}

/** "Sara reacted 😂 to your message" — WhatsApp's wording, and Umer's. */
export function reactionAlertContent(input: {
  roomName?: string;
  reactorName?: string;
  emoji: string;
}): AlertContent {
  const who = (input.reactorName ?? '').trim() || 'Someone';
  return {
    title: `💬 ${(input.roomName ?? '').trim() || 'Group'}`,
    body: `${who} reacted ${input.emoji} to your message`,
  };
}

/** `room_member_state.muted_until` → "muted right now". */
export function isMutedAt(mutedUntil: number | null | undefined, nowMs: number): boolean {
  return typeof mutedUntil === 'number' && mutedUntil > nowMs;
}

export type ReactionMap = Record<string, string[]> | null | undefined;

export interface ReactionAdd { emoji: string; userId: string }

/**
 * Which (emoji, user) pairs are in `next` but were not in `prev`.
 *
 * NOT the same function as `services/reactionAlert.diffReactionAdds`, despite
 * the name — the two tables have opposite realtime shapes and the helpers were
 * built by two workstreams for those two shapes. Here (`room_messages`, no
 * REPLICA IDENTITY FULL) a missing `prev` means "first sight", so the answer is
 * NOTHING IS NEW. There (`messages`, REPLICA IDENTITY FULL since 0025) a
 * missing `before` honestly means "it had none", so everything in `after` IS
 * new, and the "no `old` row at all" case is refused one level up by
 * `decideReactionAlert`'s `beforeKnown`. Do not unify them without unifying
 * that distinction first.
 *
 * This is the FALLBACK source for reaction alerts: `room_messages` is not
 * `replica identity full`, so a realtime UPDATE payload carries no `old` row —
 * the previous reactions map has to come from what this session last saw.
 * `prev === undefined` means "first sight of this message": nothing is new,
 * because everything already on it happened before we were listening.
 */
export function diffReactionAdds(
  prev: ReactionMap | undefined,
  next: ReactionMap,
  excludeUserId?: string | null,
): ReactionAdd[] {
  if (prev === undefined) return [];
  const before = new Set<string>();
  for (const [emoji, uids] of Object.entries(prev ?? {})) {
    for (const uid of uids ?? []) before.add(`${emoji}::${uid}`);
  }
  const out: ReactionAdd[] = [];
  for (const [emoji, uids] of Object.entries(next ?? {})) {
    for (const uid of uids ?? []) {
      if (uid === excludeUserId) continue;
      if (before.has(`${emoji}::${uid}`)) continue;
      out.push({ emoji, userId: uid });
    }
  }
  return out;
}

/** Dedupe key shared by both reaction sources (the normalized-table INSERT and
 *  the jsonb diff) so a reaction never alerts twice. */
export function reactionKey(messageId: string, userId: string, emoji: string): string {
  return `${messageId}:${userId}:${emoji}`;
}

/** `room_message_mentions` rows → "this mentions me". */
export function mentionsMeFromRows(
  rows: Array<{ kind?: string; user_id?: string | null }> | null | undefined,
  meId: string | null | undefined,
): boolean {
  if (!rows?.length || !meId) return false;
  return rows.some((r) => r.kind === 'all' || (r.kind === 'user' && r.user_id === meId));
}

/**
 * Body-parsing fallback for "does this mention me": used when
 * `room_message_mentions` is unreadable (0056 not applied, or RLS). Resolves
 * `@Name` against the ROOM's members exactly the way the bubble renders chips,
 * so what the reader sees highlighted and what pings them agree.
 */
export function mentionsMeInBody(
  body: string | null | undefined,
  opts: { members: Array<{ id: string; name?: string }>; meId: string | null | undefined },
): boolean {
  if (!body || !opts.meId) return false;
  const userMentions: RoomMention[] = [
    { kind: 'all' },
    ...opts.members
      .map((u): RoomMention => ({ kind: 'user', userId: u.id, label: userTokenName(u.name) }))
      .filter((m) => !!m.label),
  ];
  const segments = parseAllMentions(body, { userMentions });
  return segments.some((s) => s.type === 'all' || (s.type === 'user' && s.userId === opts.meId));
}
